/**
 * useLaunchReadiness — fetch-on-mount + manual refetch for the readiness
 * snapshot. No caching, no polling: the endpoint is `no-store` and the page
 * offers an explicit refresh button plus per-row RETRY.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { launchReadinessClient } from '../api/launchReadinessClient';
import type { LaunchReadinessRequestParams } from '../api/launchReadinessClient';
import type { LaunchReadinessData } from '../types/launchReadiness.types';

export interface UseLaunchReadinessResult {
  data: LaunchReadinessData | null;
  /** True only for the initial load (no data yet). */
  loading: boolean;
  /** True while a refetch is in flight and stale data is still on screen. */
  refreshing: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/** Stable default so an omitted `scope` never produces a new object identity across renders. */
const EMPTY_SCOPE: LaunchReadinessRequestParams = {};

/**
 * @param scope funnel/version/placement ids. Without a scope every
 * funnel-bound milestone reports `not_evaluated` (D-LR-1/D-LR-5) — the legacy
 * page still calls it unscoped until Plan B replaces the page.
 *
 * Refetch triggers: mount, a `projectId` change, a `scope` **value** change
 * (compared by serialized content, not object identity — a fresh object
 * literal with the same fields does not refetch), and manual `refetch()`.
 */
export function useLaunchReadiness(
  projectId: string | undefined,
  scope: LaunchReadinessRequestParams = EMPTY_SCOPE,
): UseLaunchReadinessResult {
  const [data, setData] = useState<LaunchReadinessData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const requestSeq = useRef(0);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  // Serialized so a new-but-equal scope object (new render, same values) does not retrigger
  // the effect below — only an actual value change does.
  const scopeKey = JSON.stringify(scope ?? {});

  const fetchReadiness = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    const seq = ++requestSeq.current;
    setRefreshing(true);
    setError(null);
    try {
      const fresh = await launchReadinessClient.getReadiness(projectId, scopeRef.current);
      if (seq !== requestSeq.current) return; // stale response — a newer request superseded it
      setData(fresh);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scopeKey is a serialized proxy for
    // scopeRef.current's value; it drives refetch-on-scope-change without breaking callback
    // identity on every render (scopeRef.current is read directly, not through this dep).
  }, [projectId, scopeKey]);

  useEffect(() => {
    requestSeq.current += 1; // invalidate in-flight requests from a previous project
    setData(null);
    setLoading(true);
    setError(null);
    void fetchReadiness();
  }, [fetchReadiness]);

  return { data, loading, refreshing, error, refetch: fetchReadiness };
}
